// LipSyncSection.js — AI Lip Sync: abstract face mesh on pure black
// Sticky-scroll pattern: 200vh outer, 100vh sticky inner
import { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  animate,
} from 'framer-motion';

// ---------------------------------------------------------------------------
// Face landmark data (viewBox 200 × 240)
// ---------------------------------------------------------------------------
const JAW = [
  [30, 130], [45, 158], [60, 178], [78, 198], [100, 215],
  [122, 198], [140, 178], [155, 158], [170, 130],
];

const NOSE = [
  [100, 95], [100, 130], [86, 135], [100, 138], [114, 135],
];

const RIGHT_EYE = [
  [60, 88], [68, 82], [80, 82], [88, 88], [80, 94], [68, 94],
];

const LEFT_EYE = [
  [116, 88], [124, 82], [136, 82], [144, 88], [136, 94], [124, 94],
];

const RIGHT_BROW = [[58, 72], [72, 66], [86, 68]];
const LEFT_BROW  = [[114, 68], [128, 66], [142, 72]];

// Outer mouth: top arc then bottom arc (indices 0-4 = top, 5-9 = bottom)
const MOUTH_OUTER_TOP    = [[72, 152], [84, 145], [100, 142], [116, 145], [128, 152]];
const MOUTH_OUTER_BOTTOM = [[128, 158], [116, 166], [100, 169], [84, 166], [72, 158]];

// Inner mouth
const INNER_MOUTH = [
  [82, 154], [92, 150], [100, 148], [108, 150],
  [118, 154], [110, 161], [100, 164], [90, 161],
];

// Flat list of all dot positions for stagger (label which group)
const ALL_DOTS = [
  ...JAW.map((p, i) => ({ id: `jaw-${i}`, x: p[0], y: p[1], group: 'jaw', r: 3 })),
  ...NOSE.map((p, i) => ({ id: `nose-${i}`, x: p[0], y: p[1], group: 'nose', r: 3 })),
  ...RIGHT_EYE.map((p, i) => ({ id: `reye-${i}`, x: p[0], y: p[1], group: 'reye', r: 3 })),
  ...LEFT_EYE.map((p, i) => ({ id: `leye-${i}`, x: p[0], y: p[1], group: 'leye', r: 3 })),
  ...RIGHT_BROW.map((p, i) => ({ id: `rbrow-${i}`, x: p[0], y: p[1], group: 'rbrow', r: 3 })),
  ...LEFT_BROW.map((p, i) => ({ id: `lbrow-${i}`, x: p[0], y: p[1], group: 'lbrow', r: 3 })),
  ...MOUTH_OUTER_TOP.map((p, i) => ({ id: `mot-${i}`, x: p[0], y: p[1], group: 'mouth-outer-top', r: 3 })),
  ...MOUTH_OUTER_BOTTOM.map((p, i) => ({ id: `mob-${i}`, x: p[0], y: p[1], group: 'mouth-outer-bottom', r: 3 })),
  ...INNER_MOUTH.map((p, i) => ({ id: `im-${i}`, x: p[0], y: p[1], group: 'inner-mouth', r: 1.5 })),
];

// Connecting line pairs [index, index] within ALL_DOTS array
function makeLineSegments() {
  const segs = [];
  // Helper: connect consecutive dots within a sub-array
  const chain = (dots, offset, closed = false) => {
    for (let i = 0; i < dots.length - 1; i++) segs.push([offset + i, offset + i + 1]);
    if (closed) segs.push([offset, offset + dots.length - 1]);
  };
  let off = 0;
  chain(JAW, off); off += JAW.length;
  chain(NOSE, off); off += NOSE.length;
  chain(RIGHT_EYE, off, true); off += RIGHT_EYE.length;
  chain(LEFT_EYE, off, true); off += LEFT_EYE.length;
  chain(RIGHT_BROW, off); off += RIGHT_BROW.length;
  chain(LEFT_BROW, off); off += LEFT_BROW.length;
  chain(MOUTH_OUTER_TOP, off); off += MOUTH_OUTER_TOP.length;
  chain(MOUTH_OUTER_BOTTOM, off); off += MOUTH_OUTER_BOTTOM.length;
  // Connect ends of top + bottom to close mouth corners
  // mot[0] connects to mob[4], mot[4] connects to mob[0]
  const motStart = JAW.length + NOSE.length + RIGHT_EYE.length + LEFT_EYE.length + RIGHT_BROW.length + LEFT_BROW.length;
  const mobStart = motStart + MOUTH_OUTER_TOP.length;
  segs.push([motStart, mobStart + MOUTH_OUTER_BOTTOM.length - 1]);
  segs.push([motStart + MOUTH_OUTER_TOP.length - 1, mobStart]);
  chain(INNER_MOUTH, off, true);
  return segs;
}

const LINE_SEGMENTS = makeLineSegments();

// Subtitle list
const SUBTITLES = [
  { text: 'Hello, welcome to DubLabs',       lang: 'EN' },
  { text: 'Hola, bienvenidos a DubLabs',      lang: 'ES' },
  { text: 'Bonjour, bienvenue chez DubLabs',  lang: 'FR' },
];

// Linear interpolation helper
function lerp(a, b, t) {
  return a + (b - a) * Math.min(1, Math.max(0, t));
}

// Map progress to a 0→1 range over [start, end]
function mapRange(p, start, end) {
  if (p <= start) return 0;
  if (p >= end) return 1;
  return (p - start) / (end - start);
}

// ---------------------------------------------------------------------------
// Face SVG — renders both primary (white) and ghost (red-tinted)
// ---------------------------------------------------------------------------
function FaceMesh({ p }) {
  // --- Phase values ---
  const fadeIn         = mapRange(p, 0, 0.15);      // dots appear
  const mouthOpen      = mapRange(p, 0.15, 0.45);   // mouth opens
  const ghostAppear    = mapRange(p, 0.45, 0.60);   // ghost appears
  const ghostCorrect   = mapRange(p, 0.60, 0.85);   // ghost corrects
  const synced         = mapRange(p, 0.85, 1.0);    // synced badge

  // Ghost opacity: appears in 0.45-0.60, stays until 0.85, then fades
  const ghostOpacity = (() => {
    if (p < 0.45) return 0;
    if (p < 0.60) return ghostAppear * 0.8;
    if (p < 0.85) return 0.8 - ghostCorrect * 0.8;
    return 0;
  })();

  // Ghost offset X: starts at +15, corrects to 0 over 0.60→0.85
  const ghostOffsetX = 15 * (1 - ghostCorrect);

  // Ghost mouth open: desync — ghost mouth is more open early, corrects to match
  const ghostMouthOpen = (() => {
    if (p < 0.45) return 0;
    if (p < 0.60) return ghostAppear * 0.3; // ghost starts partially open (desync)
    // correct to match mouthOpen
    return lerp(0.3, mouthOpen, ghostCorrect);
  })();

  // Build dot positions with mouth animation
  const motBaseY = MOUTH_OUTER_BOTTOM.map(pt => pt[1]);
  const imBaseY  = INNER_MOUTH.map(pt => pt[1]);

  function getDotPos(dot, isMouthOuter, isMouthInner, mouthAmount) {
    if (isMouthInner) {
      const idx = parseInt(dot.id.split('-')[1]);
      // Only bottom 4 inner mouth dots move (indices 4-7)
      if (idx >= 4) {
        return { x: dot.x, y: dot.y + mouthAmount * 14 };
      }
    }
    if (isMouthOuter) {
      const idx = parseInt(dot.id.split('-')[1]);
      return { x: dot.x, y: dot.y + mouthAmount * 18 };
    }
    return { x: dot.x, y: dot.y };
  }

  const primaryDots = ALL_DOTS.map(dot => {
    const isMob = dot.group === 'mouth-outer-bottom';
    const isIm  = dot.group === 'inner-mouth' && parseInt(dot.id.split('-')[1]) >= 4;
    return getDotPos(dot, isMob, isIm, mouthOpen);
  });

  const ghostDots = ALL_DOTS.map(dot => {
    const isMob = dot.group === 'mouth-outer-bottom';
    const isIm  = dot.group === 'inner-mouth' && parseInt(dot.id.split('-')[1]) >= 4;
    const pos = getDotPos(dot, isMob, isIm, ghostMouthOpen);
    return { ...pos, x: pos.x + ghostOffsetX };
  });

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 200 240"
      style={{ overflow: 'visible' }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id="glow-white">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-red">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Ghost connecting lines */}
      {ghostOpacity > 0 && LINE_SEGMENTS.map(([a, b], i) => (
        <line
          key={`gl-${i}`}
          x1={ghostDots[a].x} y1={ghostDots[a].y}
          x2={ghostDots[b].x} y2={ghostDots[b].y}
          stroke={`rgba(220,60,60,${0.08 * ghostOpacity})`}
          strokeWidth="0.5"
        />
      ))}

      {/* Primary connecting lines */}
      {LINE_SEGMENTS.map(([a, b], i) => (
        <line
          key={`pl-${i}`}
          x1={primaryDots[a].x} y1={primaryDots[a].y}
          x2={primaryDots[b].x} y2={primaryDots[b].y}
          stroke={`rgba(255,255,255,${0.12 * fadeIn})`}
          strokeWidth="0.5"
        />
      ))}

      {/* Ghost dots */}
      {ghostOpacity > 0 && ghostDots.map((dot, i) => (
        <circle
          key={`gd-${i}`}
          cx={dot.x}
          cy={dot.y}
          r={ALL_DOTS[i].r}
          fill={`rgba(220,60,60,${ghostOpacity * 0.7})`}
          filter="url(#glow-red)"
        />
      ))}

      {/* Primary dots with stagger fade-in + pulse */}
      {primaryDots.map((dot, i) => {
        const staggerDelay = i / ALL_DOTS.length;
        const dotFade = Math.max(0, Math.min(1, (fadeIn - staggerDelay * 0.6) / 0.4));
        return (
          <motion.circle
            key={`pd-${ALL_DOTS[i].id}`}
            cx={dot.x}
            cy={dot.y}
            r={ALL_DOTS[i].r}
            fill="white"
            filter="url(#glow-white)"
            animate={{ opacity: [0.7 * dotFade, 1 * dotFade, 0.7 * dotFade] }}
            transition={{
              duration: 2 + (i % 3) * 0.5,
              repeat: Infinity,
              delay: (i * 0.04) % 2,
              ease: 'easeInOut',
            }}
          />
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Main Section
// ---------------------------------------------------------------------------
export default function LipSyncSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  const [p, setP] = useState(0);
  const [subtitleIdx, setSubtitleIdx] = useState(0);

  useEffect(() => {
    const unsub = scrollYProgress.on('change', v => {
      setP(v);
      const idx = Math.min(2, Math.floor(v / 0.33));
      setSubtitleIdx(idx);
    });
    return unsub;
  }, [scrollYProgress]);

  const fadeIn      = mapRange(p, 0, 0.15);
  const synced      = mapRange(p, 0.85, 1.0);
  const metricAlpha = synced;

  return (
    <Box
      ref={ref}
      component="section"
      sx={{ height: '140vh', position: 'relative', background: '#000' }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          background: '#000',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Section label */}
        <Typography
          sx={{
            position: 'absolute',
            top: 32,
            left: 40,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.25)',
            zIndex: 10,
          }}
        >
          03 — LIP SYNC
        </Typography>

        {/* Subtitle that changes by scroll phase */}
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={subtitleIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: fadeIn > 0.3 ? 1 : 0, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.5 }}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,0.45)',
                textAlign: 'center',
              }}
            >
              {SUBTITLES[subtitleIdx].text}
              <span
                style={{
                  marginLeft: 10,
                  fontSize: 10,
                  letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.2)',
                }}
              >
                [{SUBTITLES[subtitleIdx].lang}]
              </span>
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* Face SVG — centered, constrained */}
        <Box
          sx={{
            width: { xs: '75vw', sm: '55vw', md: '38vw' },
            maxWidth: 440,
            aspectRatio: '200 / 240',
          }}
        >
          <FaceMesh p={p} />
        </Box>

        {/* "SYNCED" badge + metric */}
        <AnimatePresence>
          {synced > 0.3 && (
            <motion.div
              key="synced"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: synced, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                bottom: 60,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                zIndex: 10,
              }}
            >
              {/* SYNCED label */}
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.35em',
                  color: 'rgba(255,255,255,0.85)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  padding: '6px 18px',
                  borderRadius: 2,
                }}
              >
                SYNCED
              </div>
              {/* Metric badge */}
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  letterSpacing: '0.12em',
                  color: 'rgba(255,255,255,0.38)',
                }}
              >
                0 frames off sync
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subtle scan-line overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.013) 3px, rgba(255,255,255,0.013) 4px)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      </Box>
    </Box>
  );
}
